import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { SUPABASE_FUNCTIONS_URL, SUPABASE_ANON_KEY, supabase } from "@/lib/supabase";
import type { BudgetLineExtracted, ExtractedBudget } from "@/lib/offer-types";
import { BILLABLE_HOURS_PER_DAY } from "@/lib/offer-calc";

interface VisionResponse {
  success: boolean;
  model: string;
  budgets: Array<{
    name: string;
    is_optional?: boolean;
    lines: Array<{
      description: string;
      phase: BudgetLineExtracted["phase"];
      quantity: number | null;
      unit: string | null;
      amount: number;
    }>;
    offer_total: number;
    overhead_pct: number;
    discount_pct: number;
    discount_amount: number;
  }>;
  invoice_split: { startup_pct: number; final_pct: number };
  usage?: { input_tokens: number; output_tokens: number };
}

// Budsjett/tilbud regner en fakturerbar «dag» som 8 timer (BILLABLE_HOURS_PER_DAY),
// likt resten av pipelinen (offer-calc, offer-to-project, use-import-preview).
// Tidligere brukte Vision-importen 7,5 her, som ga feil timetall ved PDF-import.
// NB: kapasitet/faktureringsgrad bruker bevisst 7,5t/dag — det er en annen ting.
function daysToHours(days: number): number {
  return days * BILLABLE_HOURS_PER_DAY;
}

type LlmProvider = "claude" | "openai";

/**
 * Kaller `extract-offer-budget-vision` edge function for å hente budsjett-
 * data ut av PDF/PNG/JPG. Edge functionen prøver primær provider først
 * (default Claude), faller automatisk tilbake til OpenAI hvis primær feiler
 * og OPENAI_API_KEY-secret er satt (NB: OpenAI kan ikke lese PDF direkte).
 *
 * Returnerer samme ExtractedBudget[]-skjema som regex-parseren ga før
 * vision overtok, slik at downstream-komponenter er uendret.
 */
export function useExtractOfferBudget(options: { provider?: LlmProvider } = {}) {
  // Eksponeres så import-dialogene kan vise en inline «komprimeres»-melding
  // mens en stor PDF rasteriseres ned før selve Vision-kallet.
  const [compressing, setCompressing] = useState(false);

  const mutation = useMutation({
    mutationFn: async (inputFile: File): Promise<ExtractedBudget[]> => {
      // Store PDF-er kappes av Claude (32 MB inline) — komprimer først.
      // Filer under terskelen returneres uendret av compressPdfIfNeeded.
      const { compressPdfIfNeeded } = await import("@/lib/compress-pdf");
      let file: File;
      try {
        file = await compressPdfIfNeeded(inputFile, {
          onCompressStart: () => setCompressing(true),
        });
      } finally {
        setCompressing(false);
      }

      const formData = new FormData();
      formData.append("file", file);

      const params = new URLSearchParams();
      if (options.provider) params.set("provider", options.provider);
      const qs = params.toString();
      const url = `${SUPABASE_FUNCTIONS_URL}/extract-offer-budget-vision${qs ? `?${qs}` : ""}`;

      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(url, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${session?.access_token ?? ""}`,
          apikey: SUPABASE_ANON_KEY,
        },
        body: formData,
      });

      if (!res.ok) {
        const text = await res.text();
        throw new Error(`Vision-extraction feilet (${res.status}): ${text.slice(0, 300)}`);
      }

      const data: VisionResponse = await res.json();
      return data.budgets.map((b): ExtractedBudget => {
        const lines: BudgetLineExtracted[] = b.lines.map((l) => {
          const has_time = l.unit === "t" || l.unit === "d";
          const quantity_hours = l.quantity != null && l.unit
            ? l.unit === "d"
              ? daysToHours(l.quantity)
              : l.unit === "t"
                ? l.quantity
                : null
            : null;
          return {
            description: l.description,
            phase: l.phase,
            quantity: l.quantity,
            unit: l.unit,
            quantity_hours,
            amount: l.amount,
            has_time,
          };
        });

        const linesTotal = lines.reduce((s, l) => s + l.amount, 0);
        const overhead = linesTotal * (b.overhead_pct / 100);
        const calculated = linesTotal + overhead - b.discount_amount;
        const validation_diff = b.offer_total > 0
          ? Math.round(Math.abs(b.offer_total - calculated) * 100) / 100
          : 0;

        return {
          name: b.name,
          is_optional: b.is_optional ?? false,
          lines,
          offer_total: b.offer_total,
          overhead_pct: b.overhead_pct,
          discount_pct: b.discount_pct,
          discount_amount: b.discount_amount,
          invoice_split: data.invoice_split,
          validation_diff,
        };
      });
    },
  });

  return Object.assign(mutation, { compressing });
}
